import React from 'react';
import { Box, Button, DrawerCloseButton, DrawerWideButton, Flex, StatusIndicator, Tabs, Tab, TabList, TabPanel, Text, Tooltip, TooltipTrigger, useToast, TagBadge } from '@chatsdet/ui';
import type {
  ApiCatalogDetail,
  ApiCatalogListItem,
} from '@chatsdet/types';
import { ArrowSquareOut, ArrowsIn, PencilSimple, Check, X, CaretRight, Copy, Lightning } from '@phosphor-icons/react';
import { useOrgCredentials } from '../../../hooks/useOrgCredentials';
import { useApiRequestCredentials, useUpdateApiRequest } from '../../../features/api-catalog/api';
import { ApiTestPanel } from '../../components/api-catalog/ApiTestPanel';
import { RequestTabPanel } from '../../../features/api-catalog/components/RequestTabPanel';
import { ResponseTabPanel } from '../../../features/api-catalog/components/ResponseTabPanel';
import { ErrorOccurrenceDialog } from '../../components/api-catalog/ErrorOccurrenceDialog';
import { CopyDropdown } from '../../components/api-catalog/CopyDropdown';
import {
  MethodBadge,
  statusBadgeVariant,
  formatRelativeTime,
  formatDateTime,
  SourceTypeBadge,
  formatDuration,
} from '../../../features/api-catalog/components/shared';

type ApiDetailTab = 'overview' | 'request' | 'response' | 'credentials' | 'test';

const formatStatus = (statusCode: number | null | undefined): string => {
  if (typeof statusCode !== 'number') return '—';
  return String(statusCode);
};

/** Map a status code to a TagBadge color via the shared variant scale. */
const statusTagColor = (
  statusCode: number | null | undefined
): 'blue' | 'gray' | 'red' | 'orange' | 'teal' | 'yellow' | 'purple' => {
  const map: Record<string, 'blue' | 'gray' | 'red' | 'orange' | 'teal' | 'yellow' | 'purple'> = {
    default: 'blue',
    secondary: 'gray',
    destructive: 'red',
    outline: 'gray',
    success: 'blue',
    warning: 'orange',
  };
  return map[statusBadgeVariant(statusCode)] ?? 'gray';
};

/** Two-letter initials from a caller/credential label (derived, never mocked). */
const callerInitials = (label: string): string => {
  const parts = label.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '—';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

/** Best-effort host for the metadata grid — domain if known, else the URL host. */
const resolveHost = (domain: string | null, exampleUrl: string | null): string | null => {
  if (domain) return domain;
  if (exampleUrl) {
    try {
      return new URL(exampleUrl).host;
    } catch {
      return null;
    }
  }
  return null;
};

const healthVariant = (
  health: ApiCatalogListItem['health'] | null | undefined
): React.ComponentProps<typeof StatusIndicator>['variant'] => {
  switch (health) {
    case 'healthy':
      return 'positive';
    case 'warning':
      return 'notice';
    case 'critical':
      return 'negative';
    default:
      return 'neutral';
  }
};

const healthLabel = (health: ApiCatalogListItem['health'] | null | undefined): string => {
  switch (health) {
    case 'healthy':
      return 'Healthy';
    case 'warning':
      return 'Warning';
    case 'critical':
      return 'Critical';
    default:
      return 'Unknown';
  }
};

// ---------------------------------------------------------------------------
// Core body — shared between drawer and page
// ---------------------------------------------------------------------------

export interface ApiRequestDetailProps {
  detail: ApiCatalogDetail;
  listItem?: ApiCatalogListItem | null;
  /** Slot for caller-provided header actions (expand/collapse/close buttons). */
  headerActions?: React.ReactNode;
}

export function ApiRequestDetail({ detail, listItem, headerActions }: ApiRequestDetailProps) {
  const { toast } = useToast();
  const credentialsQuery = useOrgCredentials();
  const requestCredentialsQuery = useApiRequestCredentials(detail.id);
  const updateNameMutation = useUpdateApiRequest();
  const [selectedTab, setSelectedTab] = React.useState<ApiDetailTab>('overview');
  const hasApiCatalogFeature = true; // GA — flag deprecated
  const [showTabShadow, setShowTabShadow] = React.useState(false);
  const [selectedErrorOccurrenceId, setSelectedErrorOccurrenceId] = React.useState<string | null>(
    null
  );
  const [errorDialogOpen, setErrorDialogOpen] = React.useState(false);
  const [isEditingName, setIsEditingName] = React.useState(false);
  const [editNameValue, setEditNameValue] = React.useState('');

  React.useEffect(() => {
    setSelectedTab('overview');
    setShowTabShadow(false);
    setSelectedErrorOccurrenceId(null);
    setErrorDialogOpen(false);
    setIsEditingName(false);
  }, [detail.id]);

  React.useEffect(() => {
    setShowTabShadow(false);
  }, [selectedTab]);

  const handleTabPanelScroll = React.useCallback((event: React.UIEvent<HTMLDivElement>) => {
    setShowTabShadow(event.currentTarget.scrollTop > 0);
  }, []);

  const exampleUrl = detail.exampleUrl ?? null;
  const latestOccurrence = detail.latestOccurrence;
  const credentialNameMap = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const credential of credentialsQuery.credentials) {
      map.set(credential.id, credential.name);
    }
    return map;
  }, [credentialsQuery.credentials]);

  const credentialLabel = React.useCallback(
    (credentialId: string | null): string => {
      if (!credentialId) return 'Public';
      return credentialNameMap.get(credentialId) ?? 'Unknown credential';
    },
    [credentialNameMap]
  );

  const health = listItem?.health ?? null;

  // Derived (real) aggregates for the KPI strip + activity table.
  const recent = detail.recentOccurrences;
  const failedCount = recent.filter(
    (o) => typeof o.statusCode === 'number' && o.statusCode >= 400
  ).length;
  const hasDurations = recent.some((o) => typeof o.durationMs === 'number');
  const maxDurationMs = Math.max(
    1,
    ...recent.map((o) => (typeof o.durationMs === 'number' ? o.durationMs : 0))
  );
  const host = resolveHost(detail.domain, exampleUrl);
  const latestStatus = latestOccurrence?.statusCode ?? null;
  const latestStatusLabel = latestOccurrence?.statusLabel ?? null;

  // Shared chrome class strings (semantic tokens → renders in both brands).
  const kpiCellCls = 'bg-card px-3.5 py-3 flex flex-col gap-1 min-w-0';
  const kpiLabelCls = 'font-mono text-[10px] uppercase tracking-wider text-muted-foreground';
  const kpiValueCls = 'text-lg font-semibold text-foreground tabular-nums';
  const kpiSubCls = 'font-mono text-[11px] text-muted-foreground truncate';
  const secLabelCls =
    'font-mono text-[10.5px] uppercase tracking-wider text-muted-foreground flex items-center gap-2';
  const actGridCols = 'minmax(72px,90px) 1fr 64px 100px 104px 20px';

  const handleCopy = async (text: string, successMessage: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(successMessage);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to copy';
      toast.error(message, { reportError: false });
    }
  };

  const handleStartEditName = React.useCallback(() => {
    setEditNameValue(detail.name ?? '');
    setIsEditingName(true);
  }, [detail.name]);

  const handleSaveName = React.useCallback(() => {
    const newName = editNameValue.trim() || null;
    updateNameMutation.mutate(
      { requestId: detail.id, name: newName },
      {
        onSuccess: () => {
          setIsEditingName(false);
          toast.success('Name updated');
        },
        onError: (error) => {
          toast.error(error instanceof Error ? error.message : 'Failed to update name');
        },
      }
    );
  }, [detail.id, editNameValue, updateNameMutation, toast]);

  const handleCancelEditName = React.useCallback(() => {
    setIsEditingName(false);
  }, []);

  return (
    <Box className="h-full flex-1 flex flex-col bg-background @container">
      <header className="px-6 py-3.5 border-b space-y-3.5 sticky top-0 z-20 bg-background">
        {/* URL bar as a chip + grouped actions */}
        <Flex align="center" gap="sm">
          <div className="flex-1 min-w-0 flex items-center gap-2.5 h-[34px] px-3 rounded-md border border-border bg-muted/30">
            <MethodBadge method={detail.method} />
            {exampleUrl ? (
              <TooltipTrigger delay={200} offset={8}>
                <button
                  onClick={() => handleCopy(exampleUrl, 'Copied URL')}
                  className="flex-1 min-w-0 text-left font-mono text-[12.5px] text-foreground truncate hover:text-primary transition-colors"
                  title={exampleUrl}
                >
                  {exampleUrl}
                </button>
                <Tooltip>Click to copy URL</Tooltip>
              </TooltipTrigger>
            ) : (
              <span
                className="flex-1 min-w-0 font-mono text-[12.5px] text-foreground truncate"
                title={detail.pattern}
              >
                {detail.pattern}
              </span>
            )}
          </div>
          <Flex align="center" gap="xs" className="shrink-0">
            <Button
              variant="icon"
              iconOnly
              size="sm"
              onPress={() =>
                handleCopy(exampleUrl ?? `${detail.method} ${detail.pattern}`, 'Copied URL')
              }
              tooltip="Copy URL"
            >
              <Copy size={14} />
            </Button>
            {exampleUrl && (
              <Button
                variant="icon"
                iconOnly
                size="sm"
                onPress={() => window.open(exampleUrl, '_blank', 'noreferrer')}
                tooltip="Open in new tab"
              >
                <ArrowSquareOut size={14} />
              </Button>
            )}
            {headerActions}
          </Flex>
        </Flex>

        {/* Title (friendly name) + side meta */}
        <Flex align="end" justify="between" gap="md">
          <div className="min-w-0 flex-1">
            {host && (
              <div className="flex items-center gap-1.5 font-mono text-[10.5px] uppercase tracking-wider text-muted-foreground mb-1.5">
                <span>API catalog</span>
                <span className="opacity-50">/</span>
                <span className="truncate text-muted-foreground">{host}</span>
              </div>
            )}
            {isEditingName ? (
              <Flex align="center" gap="xs" className="flex-1">
                <input
                  autoFocus
                  value={editNameValue}
                  onChange={(e) => setEditNameValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSaveName();
                    if (e.key === 'Escape') handleCancelEditName();
                  }}
                  className="flex-1 text-lg font-semibold bg-transparent border-b border-primary outline-none py-0.5"
                  placeholder={detail.pattern}
                />
                <Button
                  variant="icon"
                  iconOnly
                  size="sm"
                  onPress={handleSaveName}
                  isDisabled={updateNameMutation.isPending}
                  tooltip="Save"
                >
                  <Check size={14} />
                </Button>
                <Button
                  variant="icon"
                  iconOnly
                  size="sm"
                  onPress={handleCancelEditName}
                  tooltip="Cancel"
                >
                  <X size={14} />
                </Button>
              </Flex>
            ) : (
              <Flex align="center" gap="xs" className="min-w-0">
                <h1 className="text-[22px] font-semibold text-foreground leading-tight tracking-tight truncate">
                  {detail.name?.trim() ? detail.name : detail.pattern}
                </h1>
                <Button
                  variant="icon"
                  iconOnly
                  size="sm"
                  onPress={handleStartEditName}
                  tooltip="Rename"
                  className="shrink-0 opacity-50 hover:opacity-100"
                >
                  <PencilSimple size={14} />
                </Button>
              </Flex>
            )}
          </div>
          <div className="shrink-0 flex items-center gap-3.5 font-mono text-[11.5px] text-muted-foreground whitespace-nowrap">
            {health && (
              <StatusIndicator size="sm" variant={healthVariant(health)}>
                {healthLabel(health)}
              </StatusIndicator>
            )}
            <span>
              Created{' '}
              <span className="text-foreground font-medium">
                {formatRelativeTime(detail.firstSeenAt)}
              </span>
            </span>
            <span>
              Last seen{' '}
              <span className="text-foreground font-medium">
                {formatRelativeTime(detail.lastSeenAt)}
              </span>
            </span>
          </div>
        </Flex>
      </header>

      <div className="flex-1 min-h-0 overflow-hidden">
        <Tabs
          className="flex flex-col h-full"
          selectedKey={selectedTab}
          onSelectionChange={(key) => setSelectedTab(String(key) as ApiDetailTab)}
        >
          <div
            className={`px-6 pt-4 pb-3 space-y-3 bg-background transition-shadow ${
              showTabShadow ? 'shadow-sm' : ''
            } relative z-10`}
          >
            <TabList aria-label="API request details">
              <Tab id="overview">Overview</Tab>
              <Tab id="request">Request</Tab>
              <Tab id="response">Response</Tab>
              <Tab id="credentials">Credentials</Tab>
              {hasApiCatalogFeature && <Tab id="test">Test</Tab>}
            </TabList>
          </div>

          <TabPanel
            id="overview"
            className="flex-1 overflow-auto p-0"
            onScroll={handleTabPanelScroll}
          >
            <div className="px-6 pt-4 pb-6 space-y-6">
              {/* KPI strip — real aggregates only */}
              <div className="grid grid-cols-2 @[640px]:grid-cols-4 gap-px bg-border rounded-md border border-border overflow-hidden">
                <div className={kpiCellCls}>
                  <span className={kpiLabelCls}>Status</span>
                  <span className={`${kpiValueCls} flex items-baseline gap-1.5`}>
                    {formatStatus(latestStatus)}
                    {latestStatusLabel && (
                      <small className="text-[11px] font-medium font-mono text-muted-foreground">
                        {latestStatusLabel}
                      </small>
                    )}
                  </span>
                  <span className={kpiSubCls}>latest call</span>
                </div>
                <div className={kpiCellCls}>
                  <span className={kpiLabelCls}>Requests</span>
                  <span className={kpiValueCls}>{detail.occurrenceCount}</span>
                  <span className={kpiSubCls}>since {formatRelativeTime(detail.firstSeenAt)}</span>
                </div>
                <div className={kpiCellCls}>
                  <span className={kpiLabelCls}>Slowest</span>
                  <span className={`${kpiValueCls} ${maxDurationMs > 1000 ? 'text-red-600' : ''}`}>
                    {hasDurations ? formatDuration(maxDurationMs) : '—'}
                  </span>
                  <span className={kpiSubCls}>
                    {hasDurations
                      ? `of last ${recent.length} call${recent.length === 1 ? '' : 's'}`
                      : 'no timing data'}
                  </span>
                </div>
                <div className={kpiCellCls}>
                  <span className={kpiLabelCls}>Health</span>
                  <span className="flex items-center h-[27px]">
                    <StatusIndicator size="sm" variant={healthVariant(health)}>
                      {healthLabel(health)}
                    </StatusIndicator>
                  </span>
                  <span className={kpiSubCls}>
                    {failedCount > 0
                      ? `${failedCount} failed of last ${recent.length}`
                      : 'no recent failures'}
                  </span>
                </div>
              </div>

              {/* Recent activity as a real table */}
              <div>
                <div className="flex items-center justify-between mb-2.5">
                  <span className={secLabelCls}>
                    Recent activity
                    {recent.length > 0 && (
                      <span className="font-mono text-[10px] px-1.5 py-px rounded bg-muted text-muted-foreground">
                        {recent.length}
                      </span>
                    )}
                  </span>
                </div>
                {recent.length === 0 ? (
                  <div className="rounded-md border border-border bg-card px-3.5 py-6 text-center font-mono text-[11.5px] text-muted-foreground">
                    No activity recorded yet.
                  </div>
                ) : (
                  <div className="rounded-md border border-border overflow-hidden bg-card">
                    <div
                      className="grid items-center gap-3 px-3.5 h-8 bg-muted/30 border-b border-border font-mono text-[10px] uppercase tracking-wider text-muted-foreground"
                      style={{ gridTemplateColumns: actGridCols }}
                    >
                      <span>When</span>
                      <span>Caller</span>
                      <span>Status</span>
                      <span>Duration</span>
                      <span>Source</span>
                      <span />
                    </div>
                    {recent.map((occ) => {
                      const isError =
                        typeof occ.statusCode === 'number' && occ.statusCode >= 400;
                      const caller = credentialLabel(occ.credentialId);
                      const slow = (occ.durationMs ?? 0) > 1000;
                      const pct =
                        typeof occ.durationMs === 'number'
                          ? Math.max(4, Math.round((occ.durationMs / maxDurationMs) * 100))
                          : 0;
                      return (
                        <div
                          key={occ.id}
                          role={isError ? 'button' : undefined}
                          onClick={
                            isError
                              ? () => {
                                  setSelectedErrorOccurrenceId(occ.id);
                                  setErrorDialogOpen(true);
                                }
                              : undefined
                          }
                          className={`group grid items-center gap-3 px-3.5 h-11 border-b border-border last:border-b-0 transition-colors ${
                            isError
                              ? 'bg-red-500/5 border-l-2 border-l-red-500 cursor-pointer hover:bg-red-500/10'
                              : 'hover:bg-muted/40'
                          }`}
                          style={{ gridTemplateColumns: actGridCols }}
                        >
                          <TooltipTrigger delay={100} offset={8}>
                            <span className="font-mono text-[11.5px] text-muted-foreground tabular-nums cursor-default">
                              {formatRelativeTime(occ.seenAt)}
                            </span>
                            <Tooltip>{formatDateTime(occ.seenAt)}</Tooltip>
                          </TooltipTrigger>
                          <span className="flex items-center gap-2 min-w-0">
                            <span className="w-[18px] h-[18px] rounded-full bg-muted text-muted-foreground text-[9px] font-semibold font-mono flex items-center justify-center shrink-0">
                              {callerInitials(caller)}
                            </span>
                            <span className="truncate text-[13px] text-foreground/90" title={caller}>
                              {caller}
                            </span>
                          </span>
                          <TagBadge
                            name={formatStatus(occ.statusCode)}
                            color={statusTagColor(occ.statusCode)}
                            hideDot
                            size="sm"
                          />
                          <span
                            className={`flex items-center gap-1.5 font-mono text-[11.5px] tabular-nums ${
                              slow ? 'text-red-600' : 'text-foreground/90'
                            }`}
                          >
                            <span className="w-[30px] h-[3px] rounded bg-border overflow-hidden relative shrink-0">
                              <span
                                className={`absolute inset-y-0 left-0 ${
                                  slow ? 'bg-red-500' : 'bg-muted-foreground'
                                }`}
                                style={{ width: `${pct}%` }}
                              />
                            </span>
                            {formatDuration(occ.durationMs)}
                          </span>
                          <SourceTypeBadge sourceType={occ.sourceType} />
                          <span
                            className={`flex justify-end ${
                              isError
                                ? 'text-muted-foreground group-hover:text-foreground'
                                : 'text-transparent'
                            }`}
                          >
                            <CaretRight size={12} />
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </TabPanel>

          <TabPanel
            id="request"
            className="flex-1 overflow-auto p-0"
            onScroll={handleTabPanelScroll}
          >
            <RequestTabPanel detail={detail} />
          </TabPanel>

          <TabPanel
            id="response"
            className="flex-1 overflow-auto p-0"
            onScroll={handleTabPanelScroll}
          >
            <ResponseTabPanel detail={detail} />
          </TabPanel>

          <TabPanel
            id="credentials"
            className="flex-1 overflow-auto p-0"
            onScroll={handleTabPanelScroll}
          >
            <div className="pb-6">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground/70 px-6 pt-4 pb-2">
                Credentials
              </p>
              {requestCredentialsQuery.isLoading ? (
                <Text className="text-muted-foreground text-xs px-6 py-2">
                  Loading credentials...
                </Text>
              ) : requestCredentialsQuery.data?.data &&
                requestCredentialsQuery.data.data.length > 0 ? (
                <div>
                  {requestCredentialsQuery.data.data.map((cred) => {
                    const name = credentialNameMap.get(cred.credentialId);
                    return (
                      <div
                        key={cred.credentialId}
                        className="flex items-center gap-3 px-6 py-1.5 text-xs hover:bg-muted/60 transition-colors"
                      >
                        <span
                          className="flex-1 min-w-0 truncate text-foreground"
                          title={name ?? cred.credentialId}
                        >
                          {name ?? 'Unknown credential'}
                        </span>
                        <TagBadge name={cred.occurrenceCount} color="gray" hideDot size="sm" />
                        <TooltipTrigger delay={100} offset={8}>
                          <span className="text-muted-foreground w-24 text-right shrink-0 cursor-default">
                            {formatRelativeTime(cred.lastUsedAt)}
                          </span>
                          <Tooltip>{formatDateTime(cred.lastUsedAt)}</Tooltip>
                        </TooltipTrigger>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <Text className="text-muted-foreground text-xs px-6 py-2">
                  No credentials have accessed this endpoint yet.
                </Text>
              )}
            </div>
          </TabPanel>

          {hasApiCatalogFeature && (
            <TabPanel
              id="test"
              className="flex-1 overflow-auto p-0"
              onScroll={handleTabPanelScroll}
            >
              <div className="px-6 pb-6">
                <ApiTestPanel
                  detail={detail}
                  credentials={requestCredentialsQuery.data?.data ?? []}
                  credentialNameMap={credentialNameMap}
                  allCredentialOptions={credentialsQuery.credentialOptions}
                />
              </div>
            </TabPanel>
          )}
        </Tabs>
      </div>

      <footer className="px-6 py-3 border-t bg-muted/30 sticky bottom-0 z-20">
        <Flex align="center" justify="between" gap="sm">
          <span className="font-mono text-[11.5px] text-muted-foreground truncate min-w-0">
            id <span className="text-foreground/80">{detail.id}</span>
          </span>
          <Flex align="center" gap="sm" className="shrink-0">
            {hasApiCatalogFeature && (
              <Button
                variant="secondary"
                size="sm"
                iconLeft={<Lightning size={14} />}
                onPress={() => setSelectedTab('test')}
              >
                Test
              </Button>
            )}
            <CopyDropdown
              items={[
                { label: 'Copy endpoint', value: `${detail.method} ${detail.pattern}` },
                ...(exampleUrl ? [{ label: 'Copy URL', value: exampleUrl }] : []),
              ]}
              onCopy={handleCopy}
            />
          </Flex>
        </Flex>
      </footer>

      <ErrorOccurrenceDialog
        requestId={detail.id}
        occurrenceId={selectedErrorOccurrenceId}
        isOpen={errorDialogOpen}
        onOpenChange={setErrorDialogOpen}
      />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Drawer wrapper — passes drawer-specific header actions
// ---------------------------------------------------------------------------

export function ApiRequestDrawer({
  detail,
  listItem,
}: {
  detail: ApiCatalogDetail;
  listItem?: ApiCatalogListItem | null;
}) {
  return (
    <ApiRequestDetail
      detail={detail}
      listItem={listItem}
      headerActions={
        <>
          <DrawerWideButton />
          <DrawerCloseButton />
        </>
      }
    />
  );
}

// ---------------------------------------------------------------------------
// Page header actions — collapse back to drawer
// ---------------------------------------------------------------------------

export function ApiRequestPageActions({ onCollapse }: { onCollapse: () => void }) {
  return (
    <Button
      variant="icon"
      iconOnly
      size="sm"
      onPress={onCollapse}
      tooltip="Collapse to drawer"
    >
      <ArrowsIn size={14} />
    </Button>
  );
}
